/**
 * Layout component with navigation
 */
import React from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import logo from '../assets/logo_Eastman_White_300dpi.png';

const Layout = () => {
  const { user, loading, isManager, isAdmin } = useUser();
  const { account, isAuthenticated, isLoading: authLoading, login, logout, resetAndLogin } = useAuth();
  const location = useLocation();

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const navigation = [
    { name: 'Home', path: '/', show: true },
    { name: 'Preferred Ship-From', path: '/preferred-ship-from', show: true },
    { name: 'Material Review', path: '/material-review', show: true },
    { name: 'Admin Dashboard', path: '/admin', show: isManager || isAdmin },
  ];

  // Get display name from Microsoft account or fall back to user context
  const displayName = account?.name || user?.UserName || user?.UserID;
  // Get email from Microsoft account or fall back to empty string
  const email = account?.username || '';

  const handleLogout = (e) => {
    e.preventDefault();
    logout();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-eastman-navy shadow-lg">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-10">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <div className="flex-shrink-0 flex items-center gap-3">
                <img src={logo} alt="Eastman" className="h-6" />
                <div className="border-l border-white/30 h-6"></div>
                <h1 className="text-white text-xl font-bold">IBP Data Management</h1>
              </div>
              <div className="hidden md:block ml-10">
                <div className="flex space-x-4">
                  {navigation.map(
                    (item) =>
                      item.show && (
                        <Link
                          key={item.name}
                          to={item.path}
                          className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                            location.pathname === item.path
                              ? 'bg-eastman-blue text-white'
                              : 'text-white/90 hover:bg-eastman-blue hover:text-white'
                          }`}
                        >
                          {item.name}
                        </Link>
                      )
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center">
              <div className="text-white text-sm flex items-center">
                <div className="flex flex-col items-end mr-4">
                  {isAuthenticated ? (
                    <>
                      <span className="font-medium">{displayName}</span>
                      {email && <span className="text-xs text-gray-300">{email}</span>}
                      <button
                        onClick={handleLogout}
                        className="text-xs text-gray-300 hover:text-white underline mt-1"
                      >
                        Logout
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="text-xs text-gray-300 mb-1">Authenticating...</span>

                      {/* Show manual login button after a delay */}
                      {(() => {
                        const authAttemptTimestamp = sessionStorage.getItem('msal_auth_attempt');
                        const currentTime = Date.now();

                        if (authAttemptTimestamp && (currentTime - parseInt(authAttemptTimestamp) > 5000)) {
                          return (
                            <button
                              onClick={resetAndLogin}
                              className="text-xs text-gray-300 hover:text-white underline"
                            >
                              Retry Login
                            </button>
                          );
                        }
                        return null;
                      })()}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-full mx-auto px-4 sm:px-6 lg:px-10 py-6">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-auto">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-10 py-4">
          <p className="text-center text-sm text-gray-500">
            IBP Data Management v1.0.0 | © 2026 Unrestricted - Internal Use Only
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
